import { Button, CircularProgress, Paper, Typography } from '@mui/material'
import { Box } from '@mui/system'
import Table from 'common/components/Table'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { getFechaFromISO } from 'common/libs/fechas'
import FileIcons from 'common/libs/FilesIcons'
import { selectPortal } from 'features/portal/portalSlice'
import Icon from 'icons/Icons'
import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { descargarDocumento, downloadFile } from 'tool/funciones'
import useFetchLaboral from '../hooks/useFetchLaboral'

/**
 * @module Documentacion
 * @module DocumentacionLaboral
 */

/**
 * Table view for the Laboral Docs
 * This is an independent view and will
 * no take the object from the menu
 *
 * @param {object} props
 * @param {number | string} [props.ejercicio] -  The year to be fetched
 * @param {string} [props.document_type ] - The tipe of document to fetch
 * @example
 * <LaboralTableView />
 */
const LaboralTableView = ({ ejercicio = null, document_type = null }) => {
  const location = useLocation()
  const [user] = useState(location?.state?.user ?? null)
  const [pageSize, setPageSize] = useState(12)
  const { response, loading } = useFetchLaboral({
    user,
    ejercicio,
    tipo_documento: document_type,
  })
  const portal = useSelector(selectPortal)
  const { handleModal } = useContext(CustomModalContext)
  const [downloading, setDownloading] = useState(null)

  const handleDownload = (res, marcar_lectura = true) => {
    handleModal({ type: ModalTypes.Custom, enabled: false })
    if (res) {
      handleModal({
        type: ModalTypes.Loading,
        enabled: true,
        content: <Typography>Descargando documento</Typography>,
      })
      downloadFile(res, marcar_lectura).finally(() => {
        handleModal({ type: ModalTypes.Loading, enabled: false })
      })
    }
  }

  return (
    <>
      <Table
        loading={loading}
        dataSource={response}
        count={response?.length ?? 1}
        onRowsPerPageChanged={setPageSize}
        rowsPerPage={pageSize}
        page={1}
        fields={[
          {
            field: 'd_rh_documento',
            title: 'Documento',
            customGetter: ({ descargado, d_rh_documento }) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Icon icon={FileIcons['pdf']?.icon} />
                  <Typography fontWeight={descargado ? 'initial' : 'bold'}>
                    {d_rh_documento}
                  </Typography>
                </Box>
              )
            },
            width: '150px',
          },
          {
            field: 'fecha_inicio',
            customGetter: ({ descargado, nom_inicio }) => {
              return (
                <Typography fontWeight={descargado ? 'initial' : 'bold'}>
                  {getFechaFromISO(nom_inicio, portal?.locale?.replaceAll('-', '_') ?? 'es-ES')}
                </Typography>
              )
            },
            title: 'Fecha Inicio',
            width: '150px',
          },
          {
            field: 'fecha_fin',
            customGetter: (row) => {
              return (
                <Typography fontWeight={row?.descargado ? 'initial' : 'bold'}>
                  {getFechaFromISO(row?.nom_fin, portal?.locale?.replaceAll('-', '_') ?? 'es-ES')}
                </Typography>
              )
            },
            title: 'Fecha Fin',
            width: '150px',
          },
          {
            field: 'actions',
            title: 'Descargar',
            width: 150,
            flex: 0.7,
            customGetter: (document) => {
              return (
                <Box sx={{ width: '100%', display: 'flex', placeItems: 'center', gap: '2px' }}>
                  <Button
                    color={FileIcons['pdf']?.color}
                    sx={{
                      backgroundColor: 'transparent',
                      px: 2,
                      color: FileIcons['pdf']?.color,
                      ['&:hover']: { backgroundColor: 'initial' },
                    }}
                    startIcon={
                      <>
                        {downloading === document?.id ? (
                          <CircularProgress size="14px" color={FileIcons['pdf']?.color} />
                        ) : (
                          <Icon
                            icon="fi-rr-arrow-circle-down"
                            color={FileIcons['pdf']?.color}
                            size="14px"
                          />
                        )}
                      </>
                    }
                    onClick={async () => {
                      handleModal({
                        type: ModalTypes.Loading,
                        enabled: true,
                        content: <Typography>Descargando documento</Typography>,
                      })
                      setDownloading(document.id)
                      const res = await descargarDocumento(
                        document.tipo_doc_principal,
                        document.id,
                        true,
                      ).finally(() => {
                        handleModal({ type: ModalTypes.Loading, enabled: false })
                        setDownloading(null)
                      })

                      if (res.txt_previo) {
                        handleModal({
                          type: ModalTypes.Custom,
                          enabled: true,
                          content: (
                            <Paper
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: { xs: '85vh', sm: '80vh' },
                                width: { xs: '95vw', sm: 'auto' },
                              }}
                            >
                              <Box sx={{ p: 4, overflowY: 'auto', flex: 1 }}>
                                <div dangerouslySetInnerHTML={{ __html: res.txt_previo }}></div>
                              </Box>
                              <Box
                                sx={{
                                  paddingRight: 4,
                                  paddingBottom: 2,
                                  paddingTop: 1,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  flexShrink: 0,
                                }}
                              >
                                <Button
                                  color="error"
                                  onClick={() =>
                                    handleModal({ type: ModalTypes.Custom, enabled: false })
                                  }
                                >
                                  Cerrar
                                </Button>
                                <Button onClick={() => handleDownload(res)}>Aceptar</Button>
                              </Box>
                            </Paper>
                          ),
                        })
                      } else {
                        handleDownload(res, false)
                      }
                    }}
                  >
                    PDF
                  </Button>
                  {document.has_xml > 0 ? (
                    <Button
                      sx={{ background: 'transparent', px: 2, color: FileIcons['xml']?.color }}
                      color="warning"
                      onClick={() => {
                        setDownloading(document.id + 'xml')
                        handleModal({ type: ModalTypes.Loading, enabled: true })
                        descargarDocumento(document.tipo_doc_secundario, document.id).finally(
                          () => {
                            handleModal({ type: ModalTypes.Loading, enabled: false })
                            setDownloading(null)
                          },
                        )
                      }}
                      startIcon={
                        <>
                          {downloading === document?.id + 'xml' ? (
                            <CircularProgress size="14px" color={FileIcons['xml']?.color} />
                          ) : (
                            <Icon
                              icon="fi-rr-arrow-circle-down"
                              color={FileIcons['xml']?.color}
                              size="14px"
                            />
                          )}
                        </>
                      }
                    >
                      XML
                    </Button>
                  ) : (
                    <></>
                  )}
                </Box>
              )
            },
          },
        ]}
      />
    </>
  )
}

export default LaboralTableView
